import React, {useState} from 'react';
import styled from 'styled-components';
import {Button, DropdownList, SearchField, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {ValueSource} from './valueSources/valueSource.types';

const tokens = tokensForThemeProvider.sky.sys;

/** The most unselected options the list renders at once. Each row is a design-system menu item of
 * several DOM nodes, so a source with tens of thousands of candidates would otherwise mount every
 * one and stall the browser while the dropdown opens. Past this many matches the reader is asked to
 * narrow the search instead. Selected values are rendered on top of this and never count toward it. */
export const MAX_RENDERED_OPTIONS = 200;

export interface ConditionValuePickerProps {
	source: ValueSource;
	selectedIds: string[];
	onChange: (ids: string[]) => void;
	/** Rendered above the search field. Null (or omitted) when the field offers no operator choice.
	 * A truthy element renders a divider beneath it, so a caller with no operator choice to offer
	 * must pass a literal `null` here rather than an element that itself renders `null` (that would
	 * still be truthy and render a divider with nothing above it). */
	operatorPicker?: JSX.Element | null;
}

/** `DropdownList.Content` pads its content vertically only. The operator control and the search
 * field are bare design-system inputs, so they need this horizontal padding or they render flush
 * against the dropdown's edges. The value rows deliberately DO NOT get it: they have to span the
 * full width so the hover highlights the whole row, and `DropdownList.CheckboxItem` supplies its
 * own 16px padding through `selectableItemCss`.
 *
 * A plain `div`, not `styled(FlexColumn)`: `FlexColumn` destructures `data-cy` out of its props
 * and never forwards it to the underlying element, so a `data-cy` passed to it is silently
 * dropped and never reaches the DOM. */
const InsetGroup = styled.div`
	display: flex;
	flex-direction: column;
	padding: 0 ${tokens.spacing['400']};
`;

/** Same padding as `InsetGroup`, plus the vertical gap between the error message and the retry
 * button — expressed as a CSS `gap` rather than `FlexColumn`'s `gap` prop, since a plain `div`
 * takes no such prop. 4px, matching the design system's smallest named gap. */
const InsetGroupWithGap = styled(InsetGroup)`
	gap: ${tokens.spacing['100']};
`;

/** The design system exports no divider component. This 1px rule, combined with the surrounding
 * gap, reproduces the confirmed 16px divider slot between sections. */
const Divider = styled.div`
	height: 1px;
	flex: none;
	background: ${tokens.color.border.decorative};
`;

/** The Select all divider sits inside `RowList`, which is `gap: 0` so the value rows below it stay
 * contiguous. That means it is the one divider with no surrounding gap to borrow spacing from, and
 * without this it renders flush against both neighbours while the two above it sit in a 16px slot.
 * The margin restores that slot — 8px either side of the rule — so all three read the same. */
const SectionDivider = styled(Divider)`
	margin: ${tokens.spacing['200']} 0;
`;

/** Caps the picker to the space the dropdown actually has, so it can never be positioned partly
 * off-screen. Radix clamps content that fits neither above nor below the trigger half out of the
 * viewport rather than shrinking it, and publishes the room it measured as
 * `--radix-dropdown-menu-content-available-height` on the content element for exactly this case.
 *
 * Capped at 490px as well as by that variable, because `DropdownList.Content` carries its own fixed
 * `max-height: 490px` with `overflow-y: auto`. Take only the variable and, wherever more than ~506px
 * is available, this element grows taller than the content that holds it — so the CONTENT scrolls
 * instead, carrying the operator control and search field out of view with the list. The pinned
 * header only holds while this element stays within the content's own cap.
 *
 * The 18px is that element's chrome, measured rather than assumed: 8px padding and a 1px border top
 * and bottom, against `box-sizing: border-box`.
 *
 * `min-height: 0` is required: a flex child defaults to `min-height: auto`, which refuses to
 * shrink below its content and would defeat the cap. */
const PickerShell = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${tokens.spacing['200']};
	min-height: 0;
	max-height: calc(min(var(--radix-dropdown-menu-content-available-height, 490px), 490px) - 18px);
`;

/** The value rows must render contiguous, not gapped: the confirmed row pitch is exactly the
 * 44px row height, with nothing between rows. `FlexColumn` always applies a gap (falling back
 * to a default when none is given), so this sets it to `0` explicitly rather than relying on
 * that fallback — a gap here would also break the full-row hover reading as one continuous
 * list. */
const RowList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0;

	/* Takes whatever vertical space the shell's cap leaves after the operator control, the search
	 * field and their dividers, and scrolls within it. Letting flexbox do that subtraction is what
	 * keeps this free of a hand-tuned header offset that would drift as the header changes.
	 * min-height 0 for the same reason as on the shell: a flex child will not shrink below its
	 * content height without it. */
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
`;

/** The Select all label and its count share the row: the label on the left, the count pushed to the
 * right edge. `flex: 1` makes the pair span what the checkbox leaves of the row. */
const SelectAllRow = styled.span`
	display: flex;
	flex: 1;
	min-width: 0;
	align-items: center;
	justify-content: space-between;
	gap: ${tokens.spacing['200']};
`;

/** A plain span, because `Text` does not forward `data-cy`. */
const SelectAllCount = styled.span`
	flex: none;
`;

/** Search + checkbox multi-select over a `ValueSource`'s candidates. The checkbox list is fully
 * driven by `source.useCandidates(search)` — this component holds no candidate state of its own,
 * only the local search text. */
export function ConditionValuePicker({source, selectedIds, onChange, operatorPicker}: ConditionValuePickerProps): JSX.Element {
	const {formatMessage} = useIntl();
	const [search, setSearch] = useState('');
	const {items, loading, error, retry, total, hasMore, pending} = source.useCandidates(search, selectedIds);

	// Whether the reader has typed anything. Blank space is no search: it narrows nothing.
	const searching = search.trim() !== '';
	const selectedLabels = source.resolveLabels(selectedIds);
	const selectedSet = new Set(selectedIds);
	const itemIds = new Set(items.map(item => item.id));
	const exclusiveIds = source.exclusiveIds ?? [];
	const unselectedMatches = items.filter(item => !selectedSet.has(item.id));
	// An exclusive row ("No assignee") is not one of the records being searched: it sits ahead of
	// them, so it neither takes one of the capped places nor counts toward the total in the hint.
	const unselectedRegular = unselectedMatches.filter(item => !exclusiveIds.includes(item.id));
	// A source that searches on the server returns one page and says whether it holds more. Its total
	// cannot decide that alone: it also counts selected matches, and rows the source could not offer.
	const truncated = hasMore === true || unselectedRegular.length > MAX_RENDERED_OPTIONS;
	const renderedUnselected = Math.min(unselectedRegular.length, MAX_RENDERED_OPTIONS);
	// The count the hint reports. The server total counts the selected values among the matches too,
	// so those on the page come off it to compare like with like. An exclusive row is no part of the
	// server's count, so a selected one is not taken off it.
	const selectedRegularCount = items.filter(item => selectedSet.has(item.id) && !exclusiveIds.includes(item.id)).length;
	const unselectedTotal =
		total === undefined ? unselectedRegular.length : Math.max(total - selectedRegularCount, unselectedRegular.length);
	// Never over an earlier answer narrowed in memory, whose numbers say nothing about this search.
	const showRefineHint = truncated && !pending && unselectedTotal > renderedUnselected;

	// The candidates in the source's own order, a ticked one where it sorts rather than moved to the
	// top. Only unselected regular rows count toward the cap: a ticked one has to stay on screen so it
	// can be unticked, however far down it sorts, and an exclusive row takes no capped place.
	let unselectedRoom = MAX_RENDERED_OPTIONS;
	const listedItems = items.filter(item => {
		if (selectedSet.has(item.id) || exclusiveIds.includes(item.id)) {
			return true;
		}
		unselectedRoom -= 1;
		return unselectedRoom >= 0;
	});

	// A selected value that is not among the candidates. With the search empty that is a value the
	// source no longer offers - an older stored value outside a bounded list, or every value of a typed
	// source, whose only candidate is what is in the box - and it is listed first, so clearing the
	// search always shows every selection and lets each be unticked.
	//
	// While a search is active such a value simply does not match. It is hidden, and the footer
	// counts it, so the reader knows the selection is larger than the ticked rows they can see.
	//
	// Except when the list is not the whole answer: a page with more behind it, rows past the cap, or
	// an earlier answer narrowed in memory. A selected value missing from that may well match, so it
	// stays listed and nothing claims the search hid it.
	const selectedOffList = selectedIds.filter(id => !itemIds.has(id));
	const hideOffList = searching && !truncated && !pending;
	const hiddenBySearch = hideOffList ? selectedOffList.length : 0;
	const pinned = hideOffList ? [] : selectedOffList.map(id => ({id, label: selectedLabels[id] ?? id}));
	const shown = [...pinned, ...listedItems];
	// An earlier answer narrowed to nothing is not "No matches": the answer for this search may
	// still hold some.
	const waiting = loading || (pending === true && !error && shown.length === 0);
	const hiddenMessage =
		hiddenBySearch === 0
			? null
			: formatMessage(
					{
						id:
							hiddenBySearch === selectedIds.length
								? 'ai_report_builder.conditions.hidden_by_search_all'
								: 'ai_report_builder.conditions.hidden_by_search_some',
					},
					{selected: selectedIds.length, hidden: hiddenBySearch}
			  );

	const toggle = (id: string, checked: boolean): void => {
		if (source.singleSelect) {
			onChange(checked ? [id] : []);
			return;
		}
		if (!checked) {
			onChange(selectedIds.filter(x => x !== id));
			return;
		}
		if (exclusiveIds.includes(id)) {
			onChange([id]);
			return;
		}
		onChange([...selectedIds.filter(x => !exclusiveIds.includes(x)), id]);
	};

	// Select all acts on `items`, which is what matches the search: that is what it promises to select
	// and clear. It never touches a selected value off the candidate list, which the reader cannot tick
	// back from here.
	//
	// Its checkbox reads against the same set: ticked when every candidate is selected, a dash when only
	// some are, empty when none are. Exclusive candidates stay out of it: they cannot be combined with
	// the rest.
	const selectableItems = items.filter(item => !exclusiveIds.includes(item.id));
	const selectableIds = new Set(selectableItems.map(item => item.id));
	const selectedMatchCount = selectableItems.filter(item => selectedSet.has(item.id)).length;
	const selectAllState: boolean | 'indeterminate' =
		selectedMatchCount === 0 ? false : selectedMatchCount === selectableItems.length ? true : 'indeterminate';

	const toggleAll = (): void => {
		if (selectAllState === true) {
			onChange(selectedIds.filter(id => !selectableIds.has(id)));
			return;
		}
		const kept = selectedIds.filter(id => !exclusiveIds.includes(id));
		onChange([...kept, ...unselectedRegular.map(item => item.id)]);
	};

	return (
		<PickerShell>
			{operatorPicker ? (
				<>
					<InsetGroup data-cy={'ai-report-condition-operator-group'}>{operatorPicker}</InsetGroup>
					<Divider data-cy={'ai-report-condition-divider'} />
				</>
			) : null}
			<InsetGroup data-cy={'ai-report-condition-search-group'}>
				<SearchField
					value={search}
					onChange={setSearch}
					isExpandable={false}
					width={'100%'}
					placeholder={formatMessage({id: 'common.search'})}
					data-cy={'ai-report-condition-value-search'}
				/>
			</InsetGroup>
			<Divider data-cy={'ai-report-condition-divider'} />
			{waiting ? (
				<InsetGroup>
					<Text color={'medium'}>
						<FormattedMessage id={'ai_report_builder.conditions.value_picker_loading'} />
					</Text>
				</InsetGroup>
			) : error ? (
				<InsetGroupWithGap data-cy={'ai-report-condition-value-error'}>
					<Text color={'medium'}>
						<FormattedMessage id={'ai_report_builder.conditions.value_picker_error'} />
					</Text>
					<Button emphasis={'low'} onPress={() => retry?.()} data-cy={'ai-report-condition-value-retry'}>
						<FormattedMessage id={'common.retry'} />
					</Button>
				</InsetGroupWithGap>
			) : shown.length === 0 ? (
				<InsetGroup>
					<Text color={'medium'}>
						<FormattedMessage id={'ai_report_builder.conditions.value_picker_empty'} />
					</Text>
				</InsetGroup>
			) : (
				<>
					{/* Above the rows rather than after them: after 200 rows it would only be seen by a
					 * reader who had already scrolled through every one. */}
					{showRefineHint ? (
						<InsetGroup data-cy={'ai-report-condition-value-refine'}>
							<Text color={'medium'}>
								<FormattedMessage
									id={'ai_report_builder.conditions.value_picker_refine'}
									values={{shown: renderedUnselected, total: unselectedTotal}}
								/>
							</Text>
						</InsetGroup>
					) : null}
					<RowList>
						{/* Hidden when there are no candidates, even where `shown` still lists selected values
						 * off the candidate list. Select all acts on the candidates, so with none it has
						 * nothing to select or clear: it would render unticked above a list where every row is
						 * ticked, and clicking it would do nothing at all.
						 *
						 * Hidden as well while the list is capped, whether here or by the server's page: it
						 * acts on every match, so it would tick values the reader cannot see and write a
						 * condition holding thousands of them, or quietly skip the matches the page left out.
						 * And while the list is an earlier answer narrowed in memory, which may lack matches
						 * the current search has. */}
						{source.singleSelect || selectableItems.length === 0 || truncated || pending ? null : (
							<>
								<DropdownList.CheckboxItem
									checked={selectAllState}
									onChange={toggleAll}
									data-cy={'ai-report-condition-select-all'}
								>
									<SelectAllRow>
										<span>
											{formatMessage({
												id: searching
													? 'ai_report_builder.conditions.select_all_matching'
													: 'common.select_all',
											})}
										</span>
										<SelectAllCount data-cy={'ai-report-condition-select-all-count'}>
											<Text size={2} color={'medium'}>
												{searching
													? String(selectableItems.length)
													: formatMessage(
															{id: 'ai_report_builder.conditions.select_all_count'},
															{selected: selectedMatchCount, total: selectableItems.length}
													  )}
											</Text>
										</SelectAllCount>
									</SelectAllRow>
								</DropdownList.CheckboxItem>
								<SectionDivider data-cy={'ai-report-condition-divider'} />
							</>
						)}
						{shown.map(item => (
							<DropdownList.CheckboxItem
								key={item.id}
								checked={selectedSet.has(item.id)}
								onChange={() => toggle(item.id, !selectedSet.has(item.id))}
								data-cy={`ai-report-condition-value-${item.id}`}
							>
								{item.label}
							</DropdownList.CheckboxItem>
						))}
					</RowList>
				</>
			)}
			{/* Outside the scrolling row list, so it stays in view however long the list is. */}
			{hiddenMessage && !waiting && !error ? (
				<>
					<Divider data-cy={'ai-report-condition-footer-divider'} />
					<InsetGroup data-cy={'ai-report-condition-hidden-by-search'}>
						<Text size={2} color={'medium'}>
							{hiddenMessage}
						</Text>
					</InsetGroup>
				</>
			) : null}
		</PickerShell>
	);
}
